import {
    MdAddchart,
    MdOutlineAddTask,
    MdOutlineAlarm,
    MdOutlineAssignmentInd,
    MdOutlineChatBubbleOutline,
    MdOutlineCheckCircle,
    MdOutlineDelete,
    MdOutlineEdit,
    MdOutlineEventRepeat,
    MdOutlineFolderShared,
    MdOutlineGavel,
    MdOutlineMailOutline,
    MdOutlineMoveToInbox,
    MdOutlinePause,
    MdOutlinePersonAddAlt,
    MdOutlinePersonOff,
    MdOutlinePlayArrow,
    MdOutlineStorage,
    MdOutlineSwapHoriz,
    MdOutlineThumbDown,
    MdOutlineTrendingDown,
    MdOutlineVerifiedUser,
} from 'react-icons/md'
import { TbFileCheck } from 'react-icons/tb'

import type { HistoryTone } from '@/Ship/Components/History'
import type { IconType } from 'react-icons'

/** Яким тостом зустріти нове сповіщення (`SPEC-014` §6.3). */
export type NotificationToast = 'info' | 'warning'

export interface NotificationKindInfo {
    /** Підпис події — той самий, що в «Історії змін» запису. */
    label: string
    icon: IconType
    tone: HistoryTone
    toast: NotificationToast
}

/**
 * Каталог подій дзвіночка за кодом (ADR 040, `SPEC-014` §6.1, §6.3).
 *
 * Дзвіночок малює підпис, значок і тон **за кодом** (`users_notifications.code`),
 * а не за текстом заголовка: перейменували заголовок — сповіщення не втрачає
 * значка. `title` з бази — лише запасний підпис для невідомого коду.
 *
 * Підписи й тони збігаються з подіями «Історії змін» (для справ —
 * `ACTIVITY_HISTORY_EVENT_MAP`): одна подія не виглядає у двох місцях
 * по-різному. Тост — `warning` для прострочення й помилки листа, `info` для
 * решти.
 *
 * Ключі — **буквальні** коди беку (`*NotificationCode.php`), по одному на
 * рядок із відступом у 4 пробіли: їх звіряє з беком вартовий В-4
 * (`Ship/Helpers/architectureGuard.test.ts`) — і в обидва боки.
 */
export const NOTIFICATION_KINDS: Record<string, NotificationKindInfo> = {
    // Справи (`SPEC-011` §7)
    activity_assigned: { label: 'Справу призначено вам', icon: MdOutlineAssignmentInd, tone: 'blue', toast: 'info' },
    activity_overdue: { label: 'Справа прострочена', icon: MdOutlineAlarm, tone: 'red', toast: 'warning' },
    activity_completed: { label: 'Справу завершено', icon: MdOutlineCheckCircle, tone: 'green', toast: 'info' },
    // Верифікація (`SPEC-009` §11, `SPEC-010` §13)
    client_sent_to_verification: {
        label: 'Клієнта відправлено на верифікацію',
        icon: MdOutlineVerifiedUser,
        tone: 'gold',
        toast: 'info',
    },
    carrier_sent_to_verification: {
        label: 'Перевізника відправлено на верифікацію',
        icon: MdOutlineVerifiedUser,
        tone: 'gold',
        toast: 'info',
    },
    // Листи (`SPEC-013`)
    letter_send_failed: { label: 'Лист не відправлено', icon: MdOutlineMailOutline, tone: 'red', toast: 'warning' },
    // Перевезення (`SPEC-025` §3.3.1): значки запиту, тендера й заявки — ті самі,
    // що в меню; підписи заявки — як у її «Історії змін»
    request_created: { label: 'Новий запит', icon: MdAddchart, tone: 'blue', toast: 'info' },
    tender_created: { label: 'Новий запит на тендер', icon: MdOutlineGavel, tone: 'blue', toast: 'info' },
    application_created: { label: 'Заявку створено', icon: TbFileCheck, tone: 'blue', toast: 'info' },
    application_confirmed: { label: 'Заявку підтверджено', icon: MdOutlineCheckCircle, tone: 'green', toast: 'info' },
    application_lost: { label: 'Заявку програно', icon: MdOutlineThumbDown, tone: 'red', toast: 'info' },
    application_offer_outbid: { label: 'Є краща ціна', icon: MdOutlineTrendingDown, tone: 'orange', toast: 'info' },
    // Завдання — модуль з демки (`SPEC-018`): підписи, значки й тони — ті, що
    // в демці розпізнавав за заголовком парсер дзвіночка
    task_created: { label: 'Нове завдання', icon: MdOutlineAddTask, tone: 'blue', toast: 'info' },
    task_assigned: { label: 'Вас призначено', icon: MdOutlinePersonAddAlt, tone: 'neutral', toast: 'info' },
    task_updated: { label: 'Змінено дані завдання', icon: MdOutlineEdit, tone: 'neutral', toast: 'info' },
    task_deadline_changed: { label: 'Змінено дедлайн', icon: MdOutlineEventRepeat, tone: 'orange', toast: 'info' },
    task_comment_added: { label: 'Новий коментар', icon: MdOutlineChatBubbleOutline, tone: 'neutral', toast: 'info' },
    task_started: { label: 'Взято в роботу', icon: MdOutlinePlayArrow, tone: 'blue', toast: 'info' },
    task_suspended: { label: 'Роботу призупинено', icon: MdOutlinePause, tone: 'gold', toast: 'info' },
    task_completed: { label: 'Завдання завершено', icon: MdOutlineCheckCircle, tone: 'green', toast: 'info' },
    task_status_changed: { label: 'Змінено статус', icon: MdOutlineSwapHoriz, tone: 'purple', toast: 'info' },
    task_overdue: { label: 'Минув дедлайн', icon: MdOutlineAlarm, tone: 'red', toast: 'warning' },
    task_removed: { label: 'Завдання видалено', icon: MdOutlineDelete, tone: 'red', toast: 'info' },
    // Диск (54631, `DISK-001` §4.14): заповнення простору — один раз на перетин порогу
    disk_quota_warning: { label: 'Диск заповнено на 80 %', icon: MdOutlineStorage, tone: 'orange', toast: 'warning' },
    disk_quota_critical: { label: 'Диск заповнено на 95 %', icon: MdOutlineStorage, tone: 'red', toast: 'warning' },
    // Диск, фаза 2: з вами поділились папкою чи файлом — клік відкриває вузол (`/disk/node/<uuid>`)
    disk_node_shared: { label: 'З вами поділились', icon: MdOutlineFolderShared, tone: 'green', toast: 'info' },
    // Диск, звільнений співробітник (`DISK-006` §4.5): адміністраторам — справа чекає рішення
    disk_offboarding_pending: {
        label: 'Диск звільненого чекає рішення',
        icon: MdOutlinePersonOff,
        tone: 'orange',
        toast: 'info',
    },
    disk_offboarding_reminder: {
        label: 'Нагадування: диск звільненого',
        icon: MdOutlineAlarm,
        tone: 'gold',
        toast: 'info',
    },
    disk_offboarding_overdue: {
        label: 'Минув строк рішення по диску',
        icon: MdOutlineAlarm,
        tone: 'red',
        toast: 'warning',
    },
    // Рішення виконано: новому власнику, колегам зі збереженим і з утраченим доступом
    disk_transfer_received: { label: 'Вам передано файли', icon: MdOutlineMoveToInbox, tone: 'blue', toast: 'info' },
    disk_owner_changed: { label: 'Новий власник файлів', icon: MdOutlineSwapHoriz, tone: 'neutral', toast: 'info' },
    disk_access_removed: { label: 'Файли видалено', icon: MdOutlineDelete, tone: 'red', toast: 'warning' },
    // Адміністратор просить звільнити місце на «Моєму диску»
    disk_cleanup_requested: {
        label: 'Звільніть місце на диску',
        icon: MdOutlineStorage,
        tone: 'orange',
        toast: 'warning',
    },
}

/** Запис каталогу за кодом; невідомий чи порожній код — `null` (нейтральний показ). */
export const notificationKindByCode = (code: string | null | undefined): NotificationKindInfo | null =>
    (code && NOTIFICATION_KINDS[code]) || null
